import {
  AmenityCategory,
  AmenitySubCategory,
  AmenityType,
  AvailableAmenity,
} from 'models/Amenity';
import { AvailableRule } from 'models/Rule';
import { useTranslation } from 'react-i18next';

export type CategorizedAmenities = {
  [category in AmenityCategory]?: {
    [subCategory in AmenitySubCategory]?: AvailableAmenity[];
  };
};

const POPULAR_AMENITIES = [
  AmenityType.HAS_TV,
  AmenityType.HAS_HEATING,
  AmenityType.HAS_KITCHEN,
  AmenityType.HAS_MICROWAVE_OVEN,
  AmenityType.HAS_COFFEE_MAKER,
  AmenityType.HAS_FRIDGE,
  AmenityType.HAS_WASHER,
  AmenityType.HAS_ESSENTIALS,
  AmenityType.HAS_SHAMPOO,
  AmenityType.HAS_HAIR_DRYER,
  AmenityType.HAS_INTERNET_WIFI,
  AmenityType.HAS_FREE_PARKING,
];

const useAmenitiesDataMap = (
  availableAmenities: AvailableAmenity[] | undefined,
  availableRules: AvailableRule[] | undefined,
) => {
  const { t } = useTranslation();

  if (!availableAmenities || !availableRules) {
    return {};
  }

  const amenityMap = new Map<string, AvailableAmenity>();
  const ruleMap = new Map<string, AvailableRule>();

  availableAmenities.forEach((amenity) => {
    if (amenity.subCategory === AmenitySubCategory.UNDEFINED) {
      amenityMap.set(amenity.amenityType, {
        ...amenity,
        subCategory: AmenitySubCategory.OTHER,
        children: [],
      });
    } else {
      amenityMap.set(amenity.amenityType, { ...amenity, children: [] });
    }
  });

  availableRules.forEach((rule) => {
    ruleMap.set(rule.rule, { ...rule, children: [] });
  });

  availableAmenities.forEach((amenity) => {
    const { groupedUnder, amenityType } = amenity;
    if (groupedUnder) {
      const parentAmenity = amenityMap.get(groupedUnder);
      const childAmenity = amenityMap.get(amenityType);
      if (parentAmenity && childAmenity) {
        parentAmenity.children!.push(childAmenity);
      }

      if (!parentAmenity && childAmenity) {
        // this is an orphaned child, it should display with new parent disabled:
        amenityMap.set(groupedUnder, {
          amenityType: groupedUnder as unknown as AmenityType,
          children: [childAmenity],
          category: childAmenity.category,
          subCategory: childAmenity.subCategory,
          channels: childAmenity.channels, // this is only for type checking
          supportsDescription: false,
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          groupedUnder: undefined,
          disabled: true,
        });
      }

      amenityMap.delete(amenityType);
    }
  });

  availableRules.forEach((rule) => {
    const { groupedUnder, rule: ruleType } = rule;
    if (groupedUnder) {
      const parentRule = ruleMap.get(groupedUnder);
      if (parentRule) {
        parentRule.children!.push(rule);
      }

      if (!parentRule) {
        // this is an orphaned child, it should display with new parent disabled:
        ruleMap.set(groupedUnder, {
          ...rule,
          disabled: true,
          groupedUnder: undefined,
        });
      }

      ruleMap.delete(ruleType);
    }
  });

  // transform the amenityMap to a categorized and sorted object
  const amenitiesWithChildren = Array.from(amenityMap.values());
  const rulesWithChildren = Array.from(ruleMap.values());

  // categorize amenities by category and subCategory
  const categorizedAmenities: CategorizedAmenities =
    amenitiesWithChildren.reduce((acc, amenity) => {
      const { category, subCategory } = amenity;

      if (!category) {
        return acc;
      }

      if (!acc[category]) {
        acc[category] = {};
      }

      if (!acc[category]![subCategory]) {
        acc[category]![subCategory] = [];
      }

      acc[category]![subCategory]!.push(amenity);

      return acc;
    }, {} as CategorizedAmenities);

  const sortAmenityTypeAlphabetically = (
    a: AvailableAmenity | AvailableRule,
    b: AvailableAmenity | AvailableRule,
  ) => {
    const aType = 'amenityType' in a ? a.amenityType : a.rule;
    const bType = 'amenityType' in b ? b.amenityType : b.rule;

    return t(`amenities.types.${aType}`).localeCompare(
      t(`amenities.types.${bType}`),
    );
  };

  Object.keys(categorizedAmenities).forEach((categoryKey) => {
    const category = categoryKey as AmenityCategory;
    const subCategories = categorizedAmenities[category];

    if (subCategories) {
      const sortedSubCategoryKeys = (
        Object.keys(subCategories) as AmenitySubCategory[]
      ).sort((a, b) => {
        // ensure "OTHER" is always the last subcategory
        if (a === AmenitySubCategory.OTHER) return 1;
        if (b === AmenitySubCategory.OTHER) return -1;

        return t(`amenities.subCategories.${a}`).localeCompare(
          t(`amenities.subCategories.${b}`),
        );
      }) as AmenitySubCategory[];

      const sortedSubCategories: {
        [subCategory in AmenitySubCategory]?: AvailableAmenity[];
      } = {};

      sortedSubCategoryKeys.forEach((subCategoryKey) => {
        sortedSubCategories[subCategoryKey] = subCategories[
          subCategoryKey
        ]!.sort(sortAmenityTypeAlphabetically);
      });

      // assign the sorted subCategories back
      categorizedAmenities[category] = sortedSubCategories;
    }
  });

  const popularAmenities = POPULAR_AMENITIES.map(
    (amenityType) => amenityMap.get(amenityType)!,
  )
    .filter((amenity) => amenity !== undefined && !amenity?.disabled)
    .toSorted(sortAmenityTypeAlphabetically);

  const categorizedRules = rulesWithChildren
    .filter((rule) => rule !== undefined && !rule?.disabled)
    .toSorted(sortAmenityTypeAlphabetically);

  return { categorizedAmenities, categorizedRules, popularAmenities };
};

export default useAmenitiesDataMap;
